import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, inject, OnInit, Output } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ChartConfiguration, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { IQuestion } from '../../../../core/models/question.model';
import { QUESTION_BUILD } from '../../../../core/constants/question-solutions';
import { OnlyNumberDirective } from '../../../../shared/directives/only-number.directive';
import { SolutionService } from '../../service/solution.service';
import { finalize } from 'rxjs';
import { CommonService } from '../../../../shared/services/common.service';
import { NgxCurrencyDirective } from 'ngx-currency';
import { AlertComponent } from '../../../../shared/components/alert/alert.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-form-build',
  templateUrl: './form-build.component.html',
  styleUrls: ['./form-build.component.scss'],
  imports: [
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    BaseChartDirective,
    OnlyNumberDirective,
    NgxCurrencyDirective,
  ],
  standalone: true,
})
export class FormBuildComponent implements OnInit {
  stepIndex: number = 1;
  formBuild: FormGroup = new FormGroup({});
  amountCalculate: number = 0;
  showResult: boolean = false;
  private solutionService = inject(SolutionService);
  private commonService = inject(CommonService);
  private dialog = inject(MatDialog);
  @Output() changeStepEvent = new EventEmitter<boolean>();
  @Output() hideLogoEvent = new EventEmitter<boolean>();
  public chartType: ChartType = 'pie';
  public chartData: ChartConfiguration['data'] = {
    labels: [
      'Gastos básicos personales',
      'Viajes y experiencias',
      'Salidas, entretenimiento, clubs',
      'Salud y seguros',
      'Regalos y similares',
      'Liquidez adicional y otros',
    ],
    datasets: [
      {
        label: 'Porcentaje: ',
        data: [40, 20, 15, 10, 5, 10],
        backgroundColor: [
          '#688A54',
          '#8A5A54',
          '#546A8A',
          '#484A40',
          '#77548A',
          '#54878A',
        ],
      },
    ],
  };
  public chartOptions: ChartConfiguration['options'] = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
        position: 'right',
        labels: {
          padding: 20,
          boxWidth: 20,
          font: {
            size: 16,
            weight: 'lighter',
            family: 'Montserrat-light',
          },
          color: '#0A3245',
        },
      },
      datalabels: {
        formatter: (value: number) => value + '%',
        color: '#fff',
        font: {
          weight: 'bold',
          family: 'Montserrat-bold',
          size: 14,
        },
      },
    },
  };

  constructor() {}

  ngOnInit() {
    this.setForm();
  }

  ngDoCheck() {
    this.updateLegendPosition();
  }

  setForm() {
    this.formBuild = new FormGroup({
      name: new FormControl('', [Validators.required]),
      age: new FormControl('', [Validators.required]),
      email: new FormControl('', [Validators.required, Validators.email]),
      country: new FormControl('', [Validators.required]),
      livePortfolio: new FormControl('', [Validators.required]),
      salaryMonthly: new FormControl('', [Validators.required]),
      budgetMonthly: new FormControl('', [Validators.required]),
      saveMonthly: new FormControl('', [Validators.required]),
      investment: new FormControl('', [Validators.required]),
    });
  }

  backStep() {
    if (this.stepIndex === 2) {
      this.stepIndex--;
    } else if (this.stepIndex === 1) {
      this.changeStepEvent.emit(true);
    }
  }

  nextStep() {
    this.stepIndex++;
    if (this.stepIndex === 3) {
      this.saveForm();
    }
  }

  saveForm(contactar: boolean = false): void {
    if (!this.formBuild.valid) {
      return;
    }
    let form: IQuestion[] = [];
    QUESTION_BUILD.forEach((item) => {
      if (this.formBuild?.value[item.value]) {
        form.push({
          question: item.question,
          answer: this.formBuild?.value[item.value],
        });
      }
    });
    this.commonService.loading.next(true);
    const payload = {
      tipo: 'Construcción de patrimonio',
      emailClient: this.formBuild?.value.email,
      contactar: contactar,
      form: form,
    };
    this.hideLogoEvent.emit(true);
    this.solutionService
      .sendForm(payload)
      .pipe(
        finalize(() => {
          setTimeout(() => {
            this.showResult = true;
            this.commonService.loading.next(false);
          }, 6000);
          if (contactar) {
            this.commonService.loading.next(false);
            this.formBuild.reset();
            this.setForm();
          }
        })
      )
      .subscribe((response) => {
        if (response) {
          if (!contactar) {
            let amountCalculate =
              ((+this.formBuild?.value.salaryMonthly * 12) / 0.07 -
                +this.formBuild?.value.budgetMonthly) *
              Math.pow(
                1.02,
                +this.formBuild?.value.livePortfolio -
                  +this.formBuild?.value.age
              );
            this.amountCalculate = +amountCalculate.toFixed(0);
          } else {
            let dialogRef = this.dialog.open(AlertComponent, {
              width: '536px',
              data: {
                buttonText: 'Volver a calcular',
              },
            });
            dialogRef.afterClosed().subscribe((result) => {
              this.resetForm();
            });
          }
        }
      });
  }

  validateteStep(): boolean {
    let disabled: boolean = false;
    switch (this.stepIndex) {
      case 1:
        disabled =
          !this.formBuild?.get('name')?.valid ||
          !this.formBuild?.get('age')?.valid ||
          !this.formBuild?.get('email')?.valid ||
          !this.formBuild?.get('country')?.valid;
        break;
      case 2:
        disabled =
          !this.formBuild?.get('livePortfolio')?.valid ||
          !this.formBuild?.get('salaryMonthly')?.valid ||
          !this.formBuild?.get('budgetMonthly')?.valid ||
          !this.formBuild?.get('saveMonthly')?.valid ||
          !this.formBuild?.get('investment')?.valid;
        break;
    }
    return disabled;
  }

  resetForm(): void {
    this.stepIndex = 1;
    this.formBuild.reset();
    this.setForm();
  }

  @HostListener('window:resize', [])
  onResize() {
    this.updateLegendPosition();
  }

  updateLegendPosition() {
    const width = window.innerWidth;
    if (width <= 920) {
      this.chartOptions!.plugins!.legend!.position = 'bottom';
      this.chartOptions!.plugins!.legend!.labels! = {
        padding: 10,
        boxWidth: 10,
        font: {
          size: 14,
          weight: 'lighter',
          family: 'Montserrat-light',
        },
        color: '#0A3245',
      }
    } else {
      this.chartOptions!.plugins!.legend!.position = 'right';
    }
  }

  get backgroundColors(): string[] {
    return this.chartData.datasets[0]?.backgroundColor as string[] || [];
  }

}
